import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertTriangle, CheckCircle2, MapPin, Hammer, Truck, X } from "lucide-react";

const POLL_INTERVAL_MS = 15_000;
const SLA_MINUTES = 60;
const ON_SITE_AUTO_DISMISS_MS = 60_000;   // banner shows "on site" for 60s then clears
const ETA_UPDATE_FLASH_MS = 12_000;       // 12s amber flash when ETA was just revised

const MCCAIN_ROLES = ["mccain", "mccain_external", "mccain_supervisor", "mccain_management"];

/**
 * Sticky top banner for McCain users tracking an in-flight Newell callout.
 * Phases (auto-derived from the /nrs/mccain-active payload):
 *   1. CALLOUT RAISED         — no acknowledged_at yet. Counts UP from received_at.
 *   2. RESPONDER CONFIRMED    — acknowledged_at present, no on_site_at. Counts DOWN to ETA target.
 *   3. ETA UPDATED            — momentary amber overlay if eta_updated_at is within 12s.
 *   4. ON SITE                — on_site_at present. Shows elapsed + SLA verdict, auto-dismisses 60s later.
 */
export default function McCainCalloutWatcher() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const lastPollRef = useRef(0);

  const poll = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      const { data } = await api.get("/nrs/mccain-active");
      setActive(data || []);
      lastPollRef.current = Date.now();
    } catch {
      // ignore; auth context handles 401
    }
  }, [user]);

  useEffect(() => {
    if (!user || !MCCAIN_ROLES.includes(user.role)) return;
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    const onFocus = () => { if (Date.now() - lastPollRef.current > 5000) poll(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, poll]);

  if (!user || !MCCAIN_ROLES.includes(user.role)) return null;
  const live = active.find((v) => !dismissedIds.has(v.visit_id));
  if (!live) return null;
  return <McCainBanner visit={live} onDismiss={() => setDismissedIds((s) => new Set([...s, live.visit_id]))} onRefresh={poll} />;
}

/* ---------- helpers ---------- */
function useTick(intervalMs = 1000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

function minutesBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  return (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60000;
}

function formatHms(mins) {
  const sign = mins < 0 ? "-" : "";
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  const s = Math.floor((abs * 60) % 60);
  if (h > 0) return `${sign}${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return `${sign}${m}m ${String(s).padStart(2, "0")}s`;
}

/* ---------- phase resolver ---------- */
function resolvePhase(visit) {
  const now = Date.now();
  if (visit.on_site_at) {
    return { kind: "on_site" };
  }
  if (!visit.acknowledged_at) {
    return { kind: "raised" };
  }
  // acknowledged, not on site
  if (visit.eta_updated_at) {
    const sinceUpdate = now - new Date(visit.eta_updated_at).getTime();
    if (sinceUpdate >= 0 && sinceUpdate <= ETA_UPDATE_FLASH_MS) {
      return { kind: "eta_updated" };
    }
  }
  return { kind: "confirmed" };
}

/* ---------- banner ---------- */
function McCainBanner({ visit, onDismiss, onRefresh }) {
  useTick(1000);
  const [autoDismissed, setAutoDismissed] = useState(false);

  useEffect(() => {
    if (!visit.on_site_at) return;
    const arrivedMsAgo = Date.now() - new Date(visit.on_site_at).getTime();
    const remaining = ON_SITE_AUTO_DISMISS_MS - arrivedMsAgo;
    if (remaining <= 0) {
      setAutoDismissed(true);
      return;
    }
    const id = setTimeout(() => { setAutoDismissed(true); onRefresh(); }, remaining);
    return () => clearTimeout(id);
  }, [visit.on_site_at, onRefresh]);

  if (autoDismissed) return null;

  const phase = resolvePhase(visit);

  // Counter logic per phase
  let counterLabel = "";
  let counterValue = "";
  let bg = "#0f172a";
  let fg = "#ffffff";
  let badgeText = "CALLOUT";
  let summary = "";

  if (phase.kind === "raised") {
    const elapsed = minutesBetween(visit.received_at, new Date().toISOString());
    counterLabel = "ELAPSED · NEWELL NOT YET ACKNOWLEDGED";
    counterValue = formatHms(elapsed);
    bg = "#dc2626"; // red — waiting for Newell
    badgeText = "CALLOUT RAISED";
    summary = "Awaiting Newell on-call response…";
  } else if (phase.kind === "confirmed" || phase.kind === "eta_updated") {
    // count DOWN to received_at + eta_minutes
    const etaTarget = new Date(new Date(visit.received_at).getTime() + (visit.eta_minutes || 0) * 60000).toISOString();
    const minsToEta = minutesBetween(new Date().toISOString(), etaTarget);
    counterLabel = minsToEta < 0 ? "ETA OVERDUE · STILL EN-ROUTE" : "ARRIVING IN";
    counterValue = formatHms(minsToEta);
    if (phase.kind === "eta_updated") {
      bg = "#d97706"; // amber — flash
      badgeText = "ETA UPDATED";
      summary = `${visit.responder_name || "Responder"} revised ETA to ~${visit.eta_minutes} min`;
    } else {
      bg = minsToEta < 0 ? "#7f1d1d" : "#1d4ed8"; // breached → dark red, otherwise blue
      badgeText = "RESPONDER CONFIRMED";
      summary = `${visit.responder_name || "Responder"} en-route · ETA ~${visit.eta_minutes} min`;
    }
  } else if (phase.kind === "on_site") {
    const elapsedMin = minutesBetween(visit.received_at, visit.on_site_at);
    const withinSla = elapsedMin <= SLA_MINUTES;
    counterLabel = "ON SITE · " + (withinSla ? "WITHIN SLA" : "SLA BREACHED");
    counterValue = `${Math.round(elapsedMin)} min`;
    bg = withinSla ? "#059669" : "#dc2626";
    badgeText = "ON SITE";
    summary = `${visit.responder_name || "Responder"} arrived — banner clears shortly`;
  }

  return (
    <div
      data-testid="mccain-callout-banner"
      data-phase={phase.kind}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        background: bg, color: fg, padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        transition: "background-color 240ms ease",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", minWidth: 140 }}>
        <div className="mono" data-testid="mccain-callout-banner-badge" style={{ fontSize: 10, letterSpacing: "0.14em", opacity: 0.9 }}>
          {phase.kind === "on_site" ? <CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} /> :
           phase.kind === "raised" ? <AlertTriangle size={11} style={{ verticalAlign: -2, marginRight: 4 }} /> :
           <Truck size={11} style={{ verticalAlign: -2, marginRight: 4 }} />}
          NEWELL CALLOUT · {badgeText}
        </div>
        <div data-testid="mccain-callout-banner-counter" className="mono" style={{ fontSize: 22, fontWeight: 900 }}>
          {counterValue}
        </div>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", opacity: 0.85 }}>{counterLabel}</div>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <MapPin size={12} />{visit.area}
          <span style={{ opacity: 0.7 }}>·</span>
          <Hammer size={12} />{visit.structure_ref}
        </div>
        <div data-testid="mccain-callout-banner-summary" style={{ fontSize: 12, opacity: 0.95, marginTop: 2 }}>
          {summary}
        </div>
        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
          {visit.description || "(no description supplied)"}
        </div>
      </div>
      {phase.kind === "on_site" ? (
        <button
          data-testid="mccain-callout-banner-dismiss"
          onClick={onDismiss}
          style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.45)", borderRadius: 3, padding: "6px 12px", fontWeight: 700, fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          <X size={11} />Dismiss
        </button>
      ) : null}
    </div>
  );
}
